// From the toolkit, not 'redux': redux is only a transitive dependency of @reduxjs/toolkit.
import type { Dispatch } from '@reduxjs/toolkit';
import { investorManagementApi } from '@/features/investor-management/investor-management.api.ts';
import { fundManagementApi } from '@/features/fund-management/fund-management.api.ts';
import { reportsApi } from '@/features/reports/reports.api.ts';

/**
 * Invalidates every cached analytics read: the investor dashboard (summary, records, NAV months),
 * investment history (investor profile), the fund dashboard and fund detail, and the NAV breakdown
 * report. Call it after any import that changes investors, investments or NAV, and after any fund or
 * share-class reference-data change (display names appear on every metrics page), so pages opened
 * next never show stale figures or names.
 *
 * Bare tags throughout: an import or rename can touch any fund or investor, so every cached entry of
 * each type is dropped, not one id. Only mounted subscriptions refetch; the rest refetch on next mount.
 *
 * `investor-management.api.ts` is imported here and also imports this helper (workbook confirm).
 * The cycle is harmless because the slices are only read when the function runs.
 */
export const invalidateAnalytics = (dispatch: Dispatch): void => {
  dispatch(
    investorManagementApi.util.invalidateTags([
      'DashboardSummary',
      'DashboardRecords',
      'DashboardNavMonths',
      'InvestmentHistory',
    ]),
  );
  dispatch(
    fundManagementApi.util.invalidateTags([
      'FundsDashboardSummary',
      'FundsDashboardRecords',
      'FundInvestors',
    ]),
  );
  dispatch(reportsApi.util.invalidateTags(['NavBreakdownReport']));
};

/**
 * `onQueryStarted` for mutations whose success makes analytics stale. Waits for the mutation, then
 * calls {@link invalidateAnalytics}. A failed mutation changes nothing, so nothing is invalidated;
 * the mutation's own error path still reports the failure.
 */
export const invalidateAnalyticsOnSuccess = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: Dispatch; queryFulfilled: Promise<unknown> },
): Promise<void> => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  invalidateAnalytics(dispatch);
};
